import { useQuery } from '@apollo/client/react';
import { useEffect, useMemo, useRef } from 'react';

import useAccount from '@zen/utils/hooks/useAccount';

import {
  GetTaskListCountersDocument,
  type GetTaskListCountersQueryResult,
  type GetTaskListCountersQueryVariables,
  TaskDetailsCountersDocument
} from '../graphql';
import type { TaskDetailsCountersSubscriptionResult } from '../graphql/taskLiskCountersUpdated.generated';
import type { SidebarCounters } from '../types';

const getCounters = (data: TaskDetailsCountersSubscriptionResult['taskDetails'] | undefined) => {
  return {
    taskListCount: data?.activeTaskCount || 0,
    allTaskListCount: data?.allOpenTaskCount || 0,
    unassignedTaskListCount: data?.allUnassignedTaskCount || 0
  };
};

const useAdminSidebarCounters = (): SidebarCounters => {
  const { userProfile } = useAccount();
  const unsubscribeRef = useRef<(() => void) | null>(null);

  const variables = useMemo(() => ({ taskDetailsFiltersInput: { onlyCurrentUsersTasks: true } }), []);

  const { data: taskListCountData, subscribeToMore } = useQuery<
    GetTaskListCountersQueryResult,
    GetTaskListCountersQueryVariables
  >(GetTaskListCountersDocument, {
    skip: !userProfile.isAdmin,
    variables
  });

  useEffect(() => {
    if (!taskListCountData || unsubscribeRef.current) return;

    unsubscribeRef.current = subscribeToMore<TaskDetailsCountersSubscriptionResult>({
      document: TaskDetailsCountersDocument,
      variables,
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData) return prev;

        return {
          taskListCount: {
            ...prev.taskListCount,
            ...subscriptionData.data?.taskDetails
          }
        };
      }
    });
  }, [subscribeToMore, taskListCountData, variables]);

  useEffect(() => {
    return () => {
      unsubscribeRef.current?.();
    };
  }, []);

  return getCounters(taskListCountData?.taskListCount);
};

export default useAdminSidebarCounters;
